import { useEffect, useState } from "react";
import { generatePath } from "react-router-dom";
import { Button, InlineAlert, LoadingSpinner, PageControlBarModule } from "../../components/BaseComponents";
import PageHeadings from "../../components/BaseComponents/PageHeadings/PageHeadings";
import PageTemplate from "../../components/BaseComponents/PageTemplate/PageTemplate";
import Pagination from "../../components/BaseComponents/Pagination/Pagination";
import SimpleSortTable, { TableColumn } from "../../components/BaseComponents/SimpleSortTable/SimpleSortTable";
import { Helmet } from "react-helmet";
import { formatDate, getApiErrorMessage } from "../../lib/utils";
import useApi from "../../hooks/useApi";
import usePagination from "../../hooks/usePagination";
import useDataSort from "../../hooks/useDataSort";
import useCleanup from "../../hooks/useCleanup";
import { useDebouncedSearch } from "../../hooks/useDebouncedSearch";
import routes from "../../components/Router/routes";
import CreateTrainingSessionModal from "./CreateTrainingSessionModal";
import EditTrainingSessionModal from "./EditTrainingSessionModal";
import DeleteTrainingSessionModal from "./DeleteTrainingSessionModal";

function AdminTrainingSessions() {
    const columns: TableColumn[] = [
        {
            id: 'settings',
            label: 'Settings',
            renderer: (key, row) => (
                <Button
                    type="Primary"
                    isLink
                    hasLeadingIcon
                    size="sm"
                    icon="fa-solid fa-sliders"
                    accessibilityLabel={`View ${row.name}`}
                    url={generatePath(routes.adminTrainingSession.path, { trainingSessionId: row.id })}
                />
            )
        },
        {
            id: 'name',
            label: 'Session name',
            sortable: true,
        },
        {
            id: 'jurisdiction_code',
            label: 'Jurisdiction',
            sortable: true,
        },
        {
            id: 'start_date',
            label: 'Start date',
            sortable: true,
            renderer: (key, row) => <span>{ row[key] ? formatDate(row[key]) : 'Starts immediately' }</span>
        },
        {
            id: 'created_at',
            label: 'Date created',
            sortable: true,
            renderer: (key, row) => <span>{ formatDate(row[key]) }</span>
        },
        {
            id: 'organisations_count',
            label: 'Org. count',
            renderer: (key, row) => <span>{ Number(row.organisations_count) || 0 }</span>,
        },
        {
            id: 'users_count',
            label: 'User count',
            renderer: (key, row) => {
                const signedUp = Number(row.users_count) || 0;
                const pending = Number(row.pending_count) || 0;
                const total = signedUp + pending;
                return pending > 0 ? <span>{total} <span className="text-charcoal-300">({pending} pending)</span></span> : <span>{total}</span>;
            },
        },
        {
            id: 'options',
            label: 'Actions',
            headerAlign: 'center',
            textAlign: 'right',
            renderer: (columnKey, row) => (
                <div className="space-x-2">
                    <Button
                        type="Primary"
                        icon="fa-solid fa-pen-to-square"
                        hasLeadingIcon
                        size="sm"
                        isTableRowButton
                        accessibilityLabel={`Edit ${row.name}`}
                        onClick={() => setEditCandidate(row)}
                    />
                    <Button
                        type="Primary"
                        icon="fa-solid fa-trash"
                        hasLeadingIcon
                        size="sm"
                        isTableRowButton
                        accessibilityLabel={`Delete ${row.name}`}
                        onClick={() => setDeletionCandidate(row)}
                    />
                </div>
            )
        },
    ]

    const api = useApi();
    const pagination = usePagination(1, 20);
    const dataSort = useDataSort('start_date', 'desc');

    const [sessions, setSessions] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');

    const [searchText, setSearchText] = useState('');
    const [debouncedSearchText, setDebouncedSearchText] = useState('');
    const { searchInProgress, flush: flushSearch } = useDebouncedSearch(searchText, () => setDebouncedSearchText(searchText))

    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editCandidate, setEditCandidate] = useState<any>(null);
    const [deletionCandidate, setDeletionCandidate] = useState<any>(null);

    const loadSessions = useCleanup(() => {
        let cancelled = false;
        const run = async () => {
            setLoading(true);
            setLoadError('');
            try {
                const qs = new URLSearchParams({
                    ...pagination.params,
                    ...dataSort.params,
                    searchText: debouncedSearchText,
                })
                const result = await api.get('admin/training-sessions?' + qs.toString())
                if (cancelled) {
                    return;
                }
                setSessions(result.data.trainingSessions);
                pagination.readMeta(result.data.meta);
            } catch (err) {
                if (!cancelled) {
                    setLoadError(getApiErrorMessage(err, 'Could not load training sessions. Please try again.'));
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        run()

        return () => {
            cancelled = true;
        };
    });

    useEffect(() => {
        loadSessions()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.params.page, pagination.params.perPage, dataSort.params.sortKey, dataSort.params.sortDirection, debouncedSearchText])

    return (
        <PageTemplate testid="admin-training-sessions-main">
            <Helmet>
                <title>{routes.adminTrainingSessions.name}</title>
            </Helmet>
            <PageHeadings title="User Training Sessions" />

            <div className="block">
                <Button
                    type="Primary"
                    text="Create new session"
                    size="sm"
                    hasTrailingIcon
                    icon="fa-solid fa-plus"
                    onClick={() => setCreateModalOpen(true)}
                    additionalClass="mt-4 mb-2"
                />
            </div>

            <PageControlBarModule.Primary>
                <PageControlBarModule.Search
                    name="searchTrainingSessions"
                    label="Search sessions"
                    placeholder="Search sessions"
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            flushSearch()
                        }
                    }}
                    onClear={() => {
                        setSearchText('')
                        flushSearch()
                    }}
                    isLoading={searchInProgress}
                />
            </PageControlBarModule.Primary>

            { loading ? (
                <LoadingSpinner />
            ) : loadError ? (
                <InlineAlert type='error' className='mt-4'>
                    <p>{loadError}</p>
                    <Button type='Primary' text='Retry' onClick={loadSessions} />
                </InlineAlert>
            ) : (
                <>
                    <SimpleSortTable
                        additionalClass="mt-4"
                        data={sessions}
                        columns={columns}
                        isLoading={loading}
                        defaultSorting={dataSort.defaultSorting}
                        onSortPress={dataSort.onChange}
                    />
                    <Pagination
                        pagination={pagination.pagination}
                        isLoading={loading}
                        onPageChange={pagination.onPageChange}
                    />
                </>
            )}

            <CreateTrainingSessionModal
                isOpen={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                onCreated={() => {
                    setCreateModalOpen(false);
                    loadSessions();
                }}
            />
            { editCandidate && (
                <EditTrainingSessionModal
                    isOpen
                    onClose={() => setEditCandidate(null)}
                    onEdited={() => {
                        setEditCandidate(null);
                        loadSessions();
                    }}
                    sessionId={editCandidate.id}
                    sessionName={editCandidate.name}
                    sessionStartDate={editCandidate.start_date}
                    sessionJurisdictionCode={editCandidate.jurisdiction_code}
                />
            )}
            { deletionCandidate && (
                <DeleteTrainingSessionModal
                    isOpen
                    onClose={() => setDeletionCandidate(null)}
                    onDeleted={() => {
                        setDeletionCandidate(null);
                        loadSessions();
                    }}
                    sessionId={deletionCandidate.id}
                    sessionName={deletionCandidate.name}
                />
            )}
        </PageTemplate>
    )
}

export default AdminTrainingSessions;
